import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { quizAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft,
  ArrowRight,
  Loader2, 
  ClipboardList,
  Clock,
  Target,
  CheckCircle,
  XCircle,
  AlertCircle,
  Play,
  RefreshCw,
  Flag
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// Script to inject in iframe srcdoc to handle anchor links and quiz submission bridge
const iframeNavScript = `<script data-nav-handler="true">
  (function() {
    var _quizBridgeSubmitted = false;

    document.addEventListener('click', function(e) {
      var link = e.target.closest('a');
      if (!link) return;
      var href = link.getAttribute('href');
      if (!href) return;
      if (href.startsWith('#')) {
        e.preventDefault();
        var target = document.getElementById(href.slice(1));
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return false;
      }
      if (href.startsWith('http://') || href.startsWith('https://')) {
        e.preventDefault();
        window.open(href, '_blank', 'noopener,noreferrer');
        return false;
      }
    }, true);

    // Collect all form answers
    function collectFormAnswers() {
      var answers = {};
      var inputs = document.querySelectorAll('input[type=radio]:checked, input[type=checkbox]:checked, input[type=text], textarea, select');
      inputs.forEach(function(input) {
        var name = input.name || input.id;
        if (!name) return;
        if (input.type === 'checkbox') {
          if (!answers[name]) answers[name] = [];
          answers[name].push(input.value);
        } else {
          answers[name] = input.value;
        }
      });
      return answers;
    }

    function sendToParent(reason) {
      if (_quizBridgeSubmitted) return;
      _quizBridgeSubmitted = true;
      var answers = collectFormAnswers();
      window.parent.postMessage({ type: 'QUIZ_SUBMITTED', reason: reason, answers: answers }, '*');
    }

    // Listen for auto-submit from parent (timer expired)
    window.addEventListener('message', function(e) {
      if (e.data && e.data.type === 'QUIZ_TIME_UP') {
        sendToParent('timeout');
      }
    });

    // Intercept form submission - use capture phase to run BEFORE template's own handler
    document.addEventListener('submit', function(e) {
      // Delay slightly to let the template's own handler collect/display results
      setTimeout(function() { sendToParent('user_click'); }, 100);
    }, true);

    // Also intercept click on submit buttons (in case e.preventDefault() blocks submit event)
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('button[type=submit], input[type=submit], .btn-submit');
      if (btn) {
        setTimeout(function() { sendToParent('user_click'); }, 300);
      }
    }, true);
  })();
<\\/script>`;

// Helper to add navigation handler to raw HTML for iframe content
const addBaseTargetToHtml = (html) => {
  if (!html) return html;
  
  let processed = html;
  
  // Add navigation handler script if not present
  if (!processed.includes('data-nav-handler')) {
    if (processed.includes('</body>')) {
      processed = processed.replace('</body>', iframeNavScript + '</body>');
    } else if (processed.includes('</html>')) {
      processed = processed.replace('</html>', iframeNavScript + '</html>');
    } else {
      processed = processed + iframeNavScript;
    }
  }
  
  // Add base target if not present
  if (!processed.includes('<base')) {
    if (processed.includes('<head>')) {
      processed = processed.replace('<head>', '<head><base target="_self">');
    } else if (processed.includes('<head ')) {
      processed = processed.replace(/<head([^>]*)>/, '<head$1><base target="_self">');
    } else if (processed.includes('<html')) {
      processed = processed.replace(/<html([^>]*)>/i, '<html$1><head><base target="_self"></head>');
    } else if (processed.includes('<!DOCTYPE')) {
      processed = processed.replace(/(<!DOCTYPE[^>]*>)/i, '$1<head><base target="_self"></head>');
    }
  }
  
  return processed;
};

export default function QuizTakePage() {
  const { workspaceId, quizId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [existingAttempt, setExistingAttempt] = useState(null);
  const [checkingAttempt, setCheckingAttempt] = useState(true);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const submittedRef = useRef(false);
  const canManage = user?.role === "admin" || user?.role === "trainer";

  useEffect(() => {
    loadQuiz();
  }, [quizId]);

  const loadQuiz = async () => {
    try {
      const response = await quizAPI.get(workspaceId, quizId);
      setQuiz(response.data);
      setTimeLeft(response.data.time_limit_minutes * 60);
      
      // Check for existing attempt (students only)
      if (!canManage) {
        try {
          const attemptsRes = await quizAPI.getMyAttempts(workspaceId, quizId);
          if (attemptsRes.data && attemptsRes.data.length > 0) {
            const best = attemptsRes.data.reduce((b, c) => c.percentage > (b?.percentage || 0) ? c : b, null);
            setExistingAttempt(best);
            setResult(best);
          }
        } catch (e) {
          // Ignore - no attempts
        }
      }
    } catch (error) {
      console.error("Failed to load quiz:", error);
      toast.error("Erreur lors du chargement du quiz");
      navigate(`/workspace/${workspaceId}/quiz`);
    } finally {
      setLoading(false);
      setCheckingAttempt(false);
    }
  };

  const handleStart = () => {
    setStarted(true);
    setQuizSubmitted(false);
    submittedRef.current = false;
    // Initialize answers for standard questions
    if (quiz.questions && quiz.questions.length > 0) {
      const initialAnswers = {};
      quiz.questions.forEach((q, index) => {
        initialAnswers[index] = q.question_type === "multiple" ? [] : "";
      });
      setAnswers(initialAnswers);
    }
  };

  const handleSelectAnswer = (questionIndex, answer) => {
    const question = quiz.questions[questionIndex];
    
    if (question.question_type === "multiple") {
      // Multiple choice - toggle selection
      const current = answers[questionIndex] || [];
      if (current.includes(answer)) {
        setAnswers({
          ...answers,
          [questionIndex]: current.filter(a => a !== answer)
        });
      } else {
        setAnswers({
          ...answers,
          [questionIndex]: [...current, answer]
        });
      }
    } else {
      // Single choice or short answer
      setAnswers({
        ...answers,
        [questionIndex]: answer
      });
    }
  };

  // Core submit function - accepts optional iframeAnswers for HTML quizzes
  
  const submitToBackend = useCallback(async (iframeAnswers) => {
    if (submittedRef.current || submitting) return;
    submittedRef.current = true;
    setSubmitting(true);
    setQuizSubmitted(true);
    
    try {
      let formattedAnswers = {};
      
      if (iframeAnswers !== null && iframeAnswers !== undefined) {
        // HTML quiz - use answers collected from iframe
        formattedAnswers = iframeAnswers;
      } else if (quiz.questions && quiz.questions.length > 0) {
        // Standard quiz - format answers from React state
        quiz.questions.forEach((q, index) => {
          const answer = answers[index];
          const questionId = q.id || `q_${index}`;
          formattedAnswers[questionId] = answer;
        });
      }
      
      const response = await quizAPI.submit(workspaceId, quizId, {
        answers: formattedAnswers
      });
      
      setResult(response.data);
      setExistingAttempt(response.data);
    } catch (error) {
      console.error("Failed to submit quiz:", error);
      if (error.response?.status === 400) {
        // Already submitted - load existing attempt
        toast.info("Tentative déjà enregistrée");
        try {
          const attemptsRes = await quizAPI.getMyAttempts(workspaceId, quizId);
          if (attemptsRes.data?.length > 0) {
            const best = attemptsRes.data.reduce((b, c) => c.percentage > (b?.percentage || 0) ? c : b, null);
            setExistingAttempt(best);
            setResult(best);
          }
        } catch (e) {}
      } else {
        toast.error("Erreur lors de la soumission");
        // Reset submission state on non-duplicate errors so user can retry
        submittedRef.current = false;
        setQuizSubmitted(false);
      }
    } finally {
      setSubmitting(false);
    }
  }, [quiz, answers, workspaceId, quizId, submitting]);

  // Wrapper for standard quiz submit button
  const handleSubmit = useCallback(async () => {
    await submitToBackend(null);
  }, [submitToBackend]);

  // Timer effect (only for standard quizzes - HTML quizzes handle their own timer)
  useEffect(() => {
    if (!started || timeLeft === null || result || quizSubmitted) return;
    // Skip timer for HTML quizzes
    if (quiz?.custom_html) return;
    
    if (timeLeft <= 0) {
      submitToBackend(null);
      return;
    }
    
    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [started, timeLeft, result, quizSubmitted, submitToBackend]);

  // Listen for iframe quiz submission events (Valider button or timeout)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'QUIZ_SUBMITTED' && !submittedRef.current) {
        const iframeAnswers = event.data.answers || {};
        toast.success(event.data.reason === 'timeout' ? 'Temps écoulé ! Soumission automatique...' : 'Quiz soumis !');
        submitToBackend(iframeAnswers);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [submitToBackend]);
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getProgressPercentage = () => {
    if (!quiz || !quiz.questions?.length) return 0;
    const answered = Object.values(answers).filter(a => 
      Array.isArray(a) ? a.length > 0 : !!a
    ).length;
    return (answered / quiz.questions.length) * 100;
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  // Results view (only for standard quizzes, HTML quizzes show results in their own iframe)
  if (result && !quiz?.custom_html) {
    return (
      <WorkspaceLayout>
        <div className="page-container animate-fade-in">
          <Card className="max-w-2xl mx-auto">
            <CardHeader className="text-center">
              <div className={`mx-auto h-20 w-20 rounded-full flex items-center justify-center mb-4 ${
                result.passed ? 'bg-green-100 dark:bg-green-900/20' : 'bg-red-100 dark:bg-red-900/20'
              }`}>
                {result.passed ? (
                  <CheckCircle className="h-10 w-10 text-green-600" />
                ) : (
                  <XCircle className="h-10 w-10 text-red-600" />
                )}
              </div>
              <CardTitle className="text-2xl">
                {result.passed ? "Félicitations !" : "Quiz terminé"}
              </CardTitle>
              <p className="text-muted-foreground">
                {result.passed 
                  ? "Vous avez réussi ce quiz !" 
                  : "Vous n'avez pas atteint le score minimum. Réessayez !"}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Score */}
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">
                  {result.percentage}%
                </div>
                <p className="text-muted-foreground">
                  {result.score} / {result.total_points} points
                </p>
              </div>
              
              {/* Progress bar */}
              <div className="space-y-2">
                <Progress value={result.percentage} className="h-3" />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Score minimum: {quiz.passing_score}%</span>
                  <span>Votre score: {result.percentage}%</span>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/10">
                  <div className="text-2xl font-bold text-green-600">
                    {result.results?.filter(r => r.is_correct).length || 0}
                  </div>
                  <div className="text-sm text-muted-foreground">Bonnes réponses</div>
                </div>
                <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/10">
                  <div className="text-2xl font-bold text-red-600">
                    {result.results?.filter(r => !r.is_correct).length || 0}
                  </div>
                  <div className="text-sm text-muted-foreground">Mauvaises réponses</div>
                </div>
                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900/10">
                  <div className="text-2xl font-bold">{quiz.questions?.length || 0}</div>
                  <div className="text-sm text-muted-foreground">Questions</div>
                </div>
              </div>
              
              {/* Review answers if show_results is enabled */}
              {quiz.show_results && result.results && (
                <div className="space-y-4">
                  <h3 className="font-semibold">Revue des réponses</h3>
                  {result.results.map((item, index) => (
                    <div 
                      key={index} 
                      className={`p-4 rounded-lg border ${
                        item.is_correct 
                          ? 'border-green-200 bg-green-50 dark:bg-green-900/10' 
                          : 'border-red-200 bg-red-50 dark:bg-red-900/10'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {item.is_correct ? (
                          <CheckCircle className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium">{item.question_text}</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Votre réponse: {Array.isArray(item.user_answer) ? item.user_answer.join(", ") : (item.user_answer || "Pas de réponse")}
                          </p>
                          {!item.is_correct && item.correct_answers && (
                            <p className="text-sm text-green-600 mt-1">
                              Bonne réponse: {Array.isArray(item.correct_answers) ? item.correct_answers.join(", ") : item.correct_answers}
                            </p>
                          )}
                          {item.explanation && (
                            <p className="text-sm text-muted-foreground mt-2 p-2 bg-white dark:bg-gray-800 rounded">
                              {item.explanation}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
            <CardFooter className="flex gap-4 justify-center">
              <Button 
                variant="outline" 
                onClick={() => navigate(`/workspace/${workspaceId}/quiz`)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour aux quiz
              </Button>
              {canManage && !existingAttempt && (
                <Button onClick={() => {
                  setResult(null);
                  setStarted(false);
                  setAnswers({});
                  setQuizSubmitted(false);
                  submittedRef.current = false;
                  setTimeLeft(quiz.time_limit_minutes * 60);
                  setCurrentQuestion(0);
                }}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Recommencer
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>
      </WorkspaceLayout>
    );
  }

  // Start screen
  if (!started) {
    // If quiz has custom HTML only (no questions), show different start screen
    const hasCustomHtmlOnly = quiz.custom_html && (!quiz.questions || quiz.questions.length === 0);
    
    return (
      <WorkspaceLayout>
        <div className="page-container animate-fade-in">
          <Card className="max-w-2xl mx-auto">
            <CardHeader className="text-center">
              <div className="mx-auto h-16 w-16 rounded-full bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center mb-4">
                <ClipboardList className="h-8 w-8 text-purple-600" />
              </div>
              <CardTitle className="text-2xl">{quiz.title}</CardTitle>
              {quiz.description && (
                <p className="text-muted-foreground mt-2">{quiz.description}</p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Quiz info */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 rounded-lg bg-muted">
                  <Target className="h-5 w-5 mx-auto mb-2 text-primary" />
                  <div className="font-semibold">{hasCustomHtmlOnly ? "HTML" : quiz.questions.length}</div>
                  <div className="text-sm text-muted-foreground">{hasCustomHtmlOnly ? "Personnalisé" : "Questions"}</div>
                </div>
                <div className="p-4 rounded-lg bg-muted">
                  <Clock className="h-5 w-5 mx-auto mb-2 text-primary" />
                  <div className="font-semibold">{quiz.time_limit_minutes} min</div>
                  <div className="text-sm text-muted-foreground">Durée</div>
                </div>
                <div className="p-4 rounded-lg bg-muted">
                  <Flag className="h-5 w-5 mx-auto mb-2 text-primary" />
                  <div className="font-semibold">{quiz.passing_score}%</div>
                  <div className="text-sm text-muted-foreground">Pour réussir</div>
                </div>
              </div>
              
              {/* Instructions */}
              <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-yellow-800 dark:text-yellow-200">Instructions</p>
                    <ul className="mt-1 text-yellow-700 dark:text-yellow-300 list-disc list-inside space-y-1">
                      {hasCustomHtmlOnly ? (
                        <li>Ce quiz utilise une interface personnalisée</li>
                      ) : (
                        <>
                          <li>Vous avez {quiz.time_limit_minutes} minutes pour compléter ce quiz</li>
                          <li>Le score minimum pour réussir est de {quiz.passing_score}%</li>
                          <li>Vous pouvez naviguer entre les questions</li>
                          {quiz.shuffle_questions && <li>Les questions sont mélangées aléatoirement</li>}
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex gap-4 justify-center">
              <Button 
                variant="outline" 
                onClick={() => navigate(`/workspace/${workspaceId}/quiz`)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour
              </Button>
              <Button onClick={handleStart} className="gap-2">
                <Play className="h-4 w-4" />
                Commencer le quiz
              </Button>
            </CardFooter>
          </Card>
        </div>
      </WorkspaceLayout>
    );
  }

  // If quiz has custom HTML, display it in an iframe
  if (quiz.custom_html) {
    return (
      <WorkspaceLayout>
        <div className="page-container animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/workspace/${workspaceId}/quiz`)}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold">{quiz.title}</h1>
                <p className="text-sm text-muted-foreground">Quiz personnalisé</p>
              </div>
            </div>
            {quizSubmitted && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                <CheckCircle className="h-4 w-4" />
                <span className="font-semibold">Soumis</span>
              </div>
            )}
          </div>

          {/* Custom HTML iframe */}
          <Card>
            <CardContent className="p-0">
              <iframe
                srcDoc={addBaseTargetToHtml(quiz.custom_html)}
                className="w-full border-0 rounded-lg"
                style={{ height: `${quiz.custom_html_height || 600}px` }}
                title={quiz.title}
                sandbox="allow-scripts allow-same-origin allow-forms"
              />
            </CardContent>
          </Card>

          {/* If there are also manual questions, show navigation to them */}
          {quiz.questions && quiz.questions.length > 0 && (
            <Card className="mt-6">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Questions supplémentaires</p>
                    <p className="text-sm text-muted-foreground">
                      Ce quiz contient également {quiz.questions.length} question(s) manuelle(s)
                    </p>
                  </div>
                  <Button onClick={() => setCurrentQuestion(0)}>
                    Voir les questions
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </WorkspaceLayout>
    );
  }

  // Quiz in progress (standard questions)
  const question = quiz.questions[currentQuestion];
  const isLastQuestion = currentQuestion === quiz.questions.length - 1;

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header with timer */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold">{quiz.title}</h1>
            <p className="text-sm text-muted-foreground">
              Question {currentQuestion + 1} sur {quiz.questions.length}
            </p>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
            timeLeft < 60 ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400' : 'bg-muted'
          }`}>
            <Clock className="h-4 w-4" />
            <span className="font-mono font-semibold">{formatTime(timeLeft)}</span>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Progression</span>
            <span>{Math.round(getProgressPercentage())}% complété</span>
          </div>
          <Progress value={getProgressPercentage()} className="h-2" />
        </div>

        {/* Question navigation dots */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {quiz.questions.map((q, index) => {
            const isAnswered = Array.isArray(answers[index]) 
              ? answers[index].length > 0 
              : !!answers[index];
            return (
              <button
                key={index}
                onClick={() => setCurrentQuestion(index)}
                className={cn(
                  "h-8 w-8 rounded-full text-sm font-medium transition-colors shrink-0",
                  index === currentQuestion 
                    ? "bg-primary text-primary-foreground" 
                    : isAnswered 
                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      : "bg-muted hover:bg-muted/80"
                )}
              >
                {index + 1}
              </button>
            );
          })}
        </div>

        {/* Question Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <Badge variant="outline">
                {question.question_type === "qcm" && "QCM - Une seule réponse"}
                {question.question_type === "multiple" && "Choix multiples - Plusieurs réponses"}
                {question.question_type === "true_false" && "Vrai ou Faux"}
                {question.question_type === "short_answer" && "Réponse courte"}
              </Badge>
              <Badge>{question.points} point{question.points > 1 ? "s" : ""}</Badge>
            </div>
            <CardTitle className="text-lg mt-4">{question.question_text}</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Options for QCM, Multiple, True/False */}
            {(question.question_type === "qcm" || question.question_type === "multiple" || question.question_type === "true_false") && (
              <div className="space-y-3">
                {question.options.map((option, optIndex) => {
                  const isSelected = question.question_type === "multiple"
                    ? (answers[currentQuestion] || []).includes(option)
                    : answers[currentQuestion] === option;
                  
                  return (
                    <button
                      key={optIndex}
                      onClick={() => handleSelectAnswer(currentQuestion, option)}
                      className={cn(
                        "w-full p-4 text-left rounded-lg border-2 transition-all",
                        isSelected 
                          ? "border-primary bg-primary/5"
                          : "border-muted hover:border-primary/50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0",
                          isSelected 
                            ? "border-primary bg-primary"
                            : "border-gray-300"
                        )}>
                          {isSelected && (
                            <CheckCircle className="h-4 w-4 text-white" />
                          )}
                        </div>
                        <span>{option}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Short Answer */}
            {question.question_type === "short_answer" && (
              <div className="space-y-2">
                <Input
                  value={answers[currentQuestion] || ""}
                  onChange={(e) => handleSelectAnswer(currentQuestion, e.target.value)}
                  placeholder="Tapez votre réponse ici..."
                  className="text-lg"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestion(prev => prev - 1)}
            disabled={currentQuestion === 0}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Précédent
          </Button>
          
          <div className="flex gap-2">
            {!isLastQuestion ? (
              <Button onClick={() => setCurrentQuestion(prev => prev + 1)}>
                Suivant
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Soumission...
                  </>
                ) : (
                  <>
                    <Flag className="h-4 w-4 mr-2" />
                    Terminer le quiz
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
}
